import { useMemo } from "react";
import { formatDistanceToNow } from "date-fns";
import { AudioLines, RefreshCw, Trash2 } from "lucide-react";
import { parseDanteChannelKey } from "@production-hub/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Router } from "@/components/router";
import type { UseConfigDataReturn } from "@/hooks/useConfigData";
import {
  buildDanteRouterModel,
  danteSourceFromPortId,
} from "@/lib/dante-router";

interface DanteTabProps {
  configData: UseConfigDataReturn;
}

export const DanteTab = ({ configData }: DanteTabProps) => {
  const { dante, danteState } = configData;
  const isOnline = configData.mode === "online";
  const model = useMemo(
    () => buildDanteRouterModel(dante, danteState),
    [dante, danteState],
  );

  const handleRoute = (targetId: string, sourceId: string | null) => {
    const target = parseDanteChannelKey(targetId);
    if (!target) return;
    const source = sourceId === null ? null : danteSourceFromPortId(sourceId);
    if (sourceId !== null && !source) return;
    configData.setDanteRoute(target.device, target.index, source);
  };

  const handleRename =
    (direction: "tx" | "rx") => (id: string, name: string | null) => {
      const channel = parseDanteChannelKey(id);
      if (!channel) return;
      configData.renameDanteChannel(
        channel.device,
        direction,
        channel.index,
        name,
      );
    };

  if (dante.devices.length === 0) {
    return (
      <Card>
        <CardContent
          className="flex flex-col items-center justify-center py-6 text-muted-foreground"
          data-testid="dante-empty"
        >
          <AudioLines className="h-8 w-8 mb-2" />
          <p>No Dante devices</p>
          <p className="text-sm">
            {isOnline
              ? "The bridge has not found any Dante devices on the studio network"
              : "Load this config to the bridge to capture the studio's Dante network"}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="dante-tab">
      <div className="flex flex-wrap items-center gap-2">
        {dante.devices.map((device) => {
          const status = danteState?.devices[device.name];
          const canRemove = !isOnline || status?.online === false;
          return (
            <Badge
              key={device.name}
              variant="outline"
              className="gap-1.5 py-1"
              data-testid={`dante-device-${device.name}`}
            >
              {status && (
                <span
                  className={
                    status.online
                      ? "h-2 w-2 rounded-full bg-green-600"
                      : "h-2 w-2 rounded-full bg-muted-foreground/40"
                  }
                  title={status.online ? `Online (${status.host})` : "Offline"}
                />
              )}
              {device.name}
              <span className="text-muted-foreground">
                {device.tx.length}×{device.rx.length}
              </span>
              {canRemove && (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => configData.removeDanteDevice(device.name)}
                  title="Remove from config"
                  data-testid={`dante-remove-${device.name}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </Badge>
          );
        })}
        <div className="ml-auto flex items-center gap-3">
          {danteState?.last_network_change_at && (
            <span className="text-xs text-muted-foreground">
              Changed on the network{" "}
              {formatDistanceToNow(
                new Date(danteState.last_network_change_at),
                {
                  addSuffix: true,
                },
              )}
            </span>
          )}
          {danteState?.last_error && (
            <span className="text-xs text-destructive">
              {danteState.last_error}
            </span>
          )}
          {configData.applyDante && (
            <Button
              size="sm"
              variant="outline"
              onClick={configData.applyDante}
              disabled={configData.isDanteApplying}
              data-testid="dante-apply"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Apply to network
            </Button>
          )}
        </div>
      </div>

      {!isOnline && (
        <p className="text-xs text-muted-foreground">
          Editing offline: changes are applied to the Dante network when this
          config is loaded to the bridge.
        </p>
      )}

      <Router
        sources={model.sources}
        targets={model.targets}
        routes={model.routes}
        routeState={model.routeState}
        routeFallbackLabel={model.routeFallbackLabel}
        onRoute={handleRoute}
        onRenameSource={handleRename("tx")}
        onRenameTarget={handleRename("rx")}
        allowClear
        viewStorageKey="dante"
        defaultView="matrix"
        sourceNoun="Transmitter"
        targetNoun="Receiver"
      />
    </div>
  );
};
